/**
 * Service Desk Overview — desk-wide health view of every unresolved
 * Freshservice ticket (all groups, all types).
 *
 * Distinct from the Application Support dashboard, which is scoped to a
 * single group. This is the "whole desk at a glance" board: total
 * unresolved, break/fix vs. requests, by group, by priority, aging,
 * and unassigned.
 *
 * Drill-in works like the Application Support dashboard: clicking any
 * metric navigates to /service-desk/tickets?f=<filter> — its own page
 * showing that slice — rather than expanding in place.
 *
 * v1 scope (Brett, May 2026): items 1–7. Inflow/outflow trend deferred
 * to v2 (needs historical resolved-date storage we don't keep yet).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Flame, Inbox, Loader2, UserX, Clock, Lightbulb } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { cn } from "@/lib/cn";
import {
  useServiceDeskOverview,
  useFreshserviceStatus,
} from "@/hooks/useFreshservice";
import {
  PRIORITY_ORDER,
  PRIORITY_COLOR,
  AGE_BUCKETS,
  daysAgo,
  isIncident,
  isMajorIncident,
  ticketsHref,
  type Filter,
} from "@/lib/serviceDeskFilter";

// CTO aging view (Jim, Jun 2026): for the Apps + Infra groups, split the
// unresolved queue into incidents (break/fix) vs enhancements (requests)
// across his four age bands, and flag each band against his targets.
const APPS_INFRA_GROUPS: { match: string; label: string }[] = [
  { match: "Application Support", label: "Application Support" },
  { match: "Network & Infrastructure Support", label: "Infrastructure" },
];

const JIM_BUCKETS = [
  { key: "0-29", label: "0–29", min: 0, max: 30 },
  { key: "30-60", label: "30–60", min: 30, max: 61 },
  { key: "61-90", label: "61–90", min: 61, max: 91 },
  { key: "91+", label: "91+", min: 91, max: Infinity },
];

// Per-type targets, as a share of that type's total within the group.
// Incidents: ≤15% in 30–60, ≤5% in 61+ (61–90 and 91+ combined).
// Enhancements: ≤25% in 30–60, ≤10% in 91+. The 61–89 gap is intentional
// (Jim's note) — that band carries no target.
const AGING_TARGETS = {
  incident: { "30-60": 15, "61plus": 5 },
  enhancement: { "30-60": 25, "91+": 10 },
};

export function ServiceDeskDashboard() {
  const statusQ = useFreshserviceStatus();
  const isConfigured = statusQ.data?.configured === true;
  const overview = useServiceDeskOverview(isConfigured);
  const navigate = useNavigate();
  const go = (f: Filter) => navigate(ticketsHref(f));
  const [openAge, setOpenAge] = useState<string | null>(null);
  const [openType, setOpenType] = useState<"incident" | "request" | null>(null);

  const tickets = overview.data?.tickets ?? [];
  const priorityLabels = overview.data?.priority_labels ?? {
    1: "Low", 2: "Medium", 3: "High", 4: "Highest",
  };

  const agg = useMemo(() => {
    const total = tickets.length;
    const incidents = tickets.filter(isIncident);
    const majorIncidents = tickets.filter(isMajorIncident);
    const requests = tickets.filter((t) => !isIncident(t));
    const unassigned = tickets.filter((t) => t.agent_id == null);

    const byPriority = PRIORITY_ORDER.map((p) => ({
      priority: p,
      label: priorityLabels[p] ?? String(p),
      count: tickets.filter((t) => t.priority === p).length,
    }));

    const groupCounts = new Map<string, number>();
    for (const t of tickets) {
      const g = t.group_name?.trim() || "No group";
      groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1);
    }
    const byGroup = Array.from(groupCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const aging = AGE_BUCKETS.map((b) => ({ ...b, count: 0 }));
    let aged30plus = 0;
    for (const t of tickets) {
      const age = daysAgo(t.created_at);
      if (age == null) continue;
      if (age >= 30) aged30plus += 1;
      for (const b of aging) {
        if (age >= b.min && age < b.max) { b.count += 1; break; }
      }
    }

    return {
      total,
      incidents: incidents.length,
      majorIncidents: majorIncidents.length,
      requests: requests.length,
      unassigned: unassigned.length,
      byPriority,
      byGroup,
      aging,
      aged30plus,
    };
  }, [tickets, priorityLabels]);

  // Aging-by-type for the Apps + Infra groups (CTO view). One row per
  // group, counts bucketed into Jim's four bands, split incident vs
  // enhancement.
  const agingByType = useMemo(() => {
    const buildRow = (g: { match: string; label: string }, isInc: boolean) => {
      const subset = tickets.filter(
        (t) =>
          (t.group_name?.trim() ?? "") === g.match &&
          isIncident(t) === isInc,
      );
      const counts = JIM_BUCKETS.map(() => 0);
      for (const t of subset) {
        const age = daysAgo(t.created_at);
        if (age == null) continue;
        const idx = JIM_BUCKETS.findIndex((b) => age >= b.min && age < b.max);
        if (idx >= 0) counts[idx] += 1;
      }
      return { label: g.label, group: g.match, total: subset.length, counts };
    };
    return {
      incident: APPS_INFRA_GROUPS.map((g) => buildRow(g, true)),
      enhancement: APPS_INFRA_GROUPS.map((g) => buildRow(g, false)),
    };
  }, [tickets]);

  // Drill-down for the Aging card: when a band is selected, break its
  // tickets out by group with the incident vs enhancement split.
  const ageBreakdown = useMemo(() => {
    if (!openAge) return null;
    const bucket = AGE_BUCKETS.find((b) => b.key === openAge);
    if (!bucket) return null;
    const inBand = tickets.filter((t) => {
      const age = daysAgo(t.created_at);
      return age != null && age >= bucket.min && age < bucket.max;
    });
    const byGroup = new Map<string, { inc: number; enh: number }>();
    for (const t of inBand) {
      const g = t.group_name?.trim() || "No group";
      const e = byGroup.get(g) ?? { inc: 0, enh: 0 };
      if (isIncident(t)) e.inc += 1;
      else e.enh += 1;
      byGroup.set(g, e);
    }
    const rows = Array.from(byGroup.entries())
      .map(([name, v]) => ({ name, inc: v.inc, enh: v.enh, total: v.inc + v.enh }))
      .sort((a, b) => b.total - a.total);
    return { key: bucket.key, label: bucket.label, total: inBand.length, rows };
  }, [openAge, tickets]);

  // Drill-down for the Break/Fix vs. Requests card: when a type is
  // selected, break its tickets out by group across the age bands
  // (0–29 / 30–60 / 61–90 / 91+).
  const typeBreakdown = useMemo(() => {
    if (!openType) return null;
    const wantInc = openType === "incident";
    const subset = tickets.filter((t) => isIncident(t) === wantInc);
    const byGroup = new Map<string, number[]>();
    for (const t of subset) {
      const age = daysAgo(t.created_at);
      if (age == null) continue;
      const idx = JIM_BUCKETS.findIndex((b) => age >= b.min && age < b.max);
      if (idx < 0) continue;
      const g = t.group_name?.trim() || "No group";
      const arr = byGroup.get(g) ?? JIM_BUCKETS.map(() => 0);
      arr[idx] += 1;
      byGroup.set(g, arr);
    }
    const rows = Array.from(byGroup.entries())
      .map(([name, counts]) => ({ name, counts, total: counts.reduce((s, c) => s + c, 0) }))
      .sort((a, b) => b.total - a.total);
    const total = rows.reduce((s, r) => s + r.total, 0);
    return { type: openType, label: wantInc ? "Incidents" : "Requests", total, rows };
  }, [openType, tickets]);

  return (
    <>
      <TopBar
        title="Service Desk"
        subtitle="Every unresolved ticket across the whole desk — incidents, requests, all groups."
      />
      <div className="space-y-6 p-8">
        {!isConfigured ? (
          <div className="rounded-xl border border-line bg-surface p-8 text-center text-sm text-ink-muted">
            Freshservice isn't configured. Set it up in Settings → Integrations.
          </div>
        ) : overview.isLoading ? (
          <div className="flex items-center justify-center rounded-xl border border-line bg-surface p-12 text-sm text-ink-muted">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Pulling the desk…
          </div>
        ) : overview.isError ? (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-700 dark:text-red-300">
            Couldn't load the service desk. {(overview.error as any)?.response?.data?.detail ?? ""}
          </div>
        ) : (
          <>
            {/* KPI strip — each tile navigates to its filtered slice. */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Kpi label="Total unresolved" value={agg.total} tone="slate" icon={<Inbox className="h-4 w-4" />} onClick={() => go({ kind: "all" })} />
              <Kpi label="Incidents (break/fix)" value={agg.incidents} tone="sky" icon={<AlertTriangle className="h-4 w-4" />} onClick={() => go({ kind: "type", value: "incident" })} />
              <Kpi label="Major incidents" value={agg.majorIncidents} tone={agg.majorIncidents > 0 ? "red" : "slate"} icon={<Flame className="h-4 w-4" />} alarm={agg.majorIncidents > 0} onClick={() => go({ kind: "major" })} />
              <Kpi label="Aged 30+ days" value={agg.aged30plus} tone={agg.aged30plus > 0 ? "amber" : "slate"} icon={<Clock className="h-4 w-4" />} alarm={agg.aged30plus > 0} onClick={() => go({ kind: "aged30" })} />
              <Kpi label="Unassigned" value={agg.unassigned} tone={agg.unassigned > 0 ? "amber" : "slate"} icon={<UserX className="h-4 w-4" />} alarm={agg.unassigned > 0} onClick={() => go({ kind: "unassigned" })} />
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <Card title="By priority">
                <div className="space-y-1">
                  {agg.byPriority.map((p) => (
                    <BarRow key={p.priority} label={p.label} count={p.count} max={agg.total}
                      barClass={PRIORITY_COLOR[p.priority]?.bar ?? "bg-slate-400"}
                      onClick={() => go({ kind: "priority", value: p.priority })} />
                  ))}
                </div>
              </Card>

              <Card title="Break/Fix vs. Requests" subtitle="Click a segment to break it out by age band and group">
                <SplitBar
                  left={{ label: "Incidents", count: agg.incidents, barClass: "bg-red-400", onClick: () => setOpenType(openType === "incident" ? null : "incident") }}
                  right={{ label: "Requests", count: agg.requests, barClass: "bg-sky-400", onClick: () => setOpenType(openType === "request" ? null : "request") }}
                />
                {typeBreakdown && (
                  <TypeBreakdown
                    data={typeBreakdown}
                    onViewTickets={() => go({ kind: "type", value: typeBreakdown.type })}
                  />
                )}
              </Card>

              <Card title="By group">
                {agg.byGroup.length === 0 ? (
                  <Empty>No groups on the unresolved queue.</Empty>
                ) : (
                  <div className="space-y-1">
                    {agg.byGroup.map((g) => (
                      <BarRow key={g.name} label={g.name} count={g.count} max={agg.total}
                        barClass="bg-brand-navy-500"
                        onClick={() => go({ kind: "group", value: g.name })} />
                    ))}
                  </div>
                )}
              </Card>

              <Card title="Aging" subtitle="Click a band to see groups + incident/enhancement split">
                <div className="space-y-1">
                  {agg.aging.map((b) => (
                    <BarRow key={b.key} label={b.label} count={b.count} max={agg.total}
                      barClass={b.bar}
                      active={openAge === b.key}
                      onClick={() => setOpenAge(openAge === b.key ? null : b.key)} />
                  ))}
                </div>
                {ageBreakdown && (
                  <AgeBreakdown
                    data={ageBreakdown}
                    onViewTickets={() => go({ kind: "age", value: ageBreakdown.key })}
                  />
                )}
              </Card>
            </div>

            <Card
              title="Aging by type — Apps & Infra"
              subtitle="Incidents (break/fix) vs. enhancements (requests), by group. Cells flagged against target."
            >
              <div className="space-y-5">
                <AgingTypeTable rows={agingByType.incident} type="incident" />
                <AgingTypeTable rows={agingByType.enhancement} type="enhancement" />
              </div>
              <p className="mt-3 text-[11px] text-ink-subtle">
                Incidents target ≤15% in 30–60 and ≤5% past 61 days · enhancements
                target ≤25% in 30–60 and ≤10% past 91 days. Small groups (n shown)
                make percentages noisy.
              </p>
            </Card>

            <p className="text-[11px] text-ink-subtle">
              Click any number, bar, or segment to open the tickets behind it. Major
              incidents = explicit "Major Incident" type, or a Highest-priority
              incident — tell me if your Freshservice flags them differently.
            </p>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Local presentational components
// ---------------------------------------------------------------------------

function Kpi({
  label, value, tone, icon, alarm, onClick,
}: {
  label: string;
  value: number;
  tone: "slate" | "sky" | "amber" | "red";
  icon: React.ReactNode;
  alarm?: boolean;
  onClick?: () => void;
}) {
  const toneClass = {
    slate: "ring-line text-ink",
    sky: "ring-sky-200 dark:ring-sky-500/30 text-sky-900 dark:text-sky-300",
    amber: "ring-amber-200 dark:ring-amber-500/30 text-amber-900 dark:text-amber-300",
    red: "ring-red-200 dark:ring-red-500/30 text-red-900 dark:text-red-300",
  }[tone];
  const iconTone = {
    slate: "text-ink-subtle",
    sky: "text-sky-500",
    amber: "text-amber-500",
    red: "text-red-500",
  }[tone];
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "rounded-xl bg-surface p-4 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md",
        toneClass,
        alarm && "ring-2",
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={iconTone}>{icon}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
          {label}
        </span>
      </div>
      <div className="mt-1 text-3xl font-bold tabular-nums">{value}</div>
    </motion.button>
  );
}

function Card({
  title, subtitle, children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-surface p-5 ring-1 ring-line">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function BarRow({
  label, count, max, barClass, onClick, active,
}: {
  label: string;
  count: number;
  max: number;
  barClass: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-raised",
        active && "bg-raised",
      )}
    >
      <span className={cn("w-28 shrink-0 truncate text-xs text-ink-3", active && "font-medium text-ink")} title={label}>{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-raised">
        <div className={cn("h-full rounded-full", barClass)} style={{ width: `${Math.max(pct, count > 0 ? 3 : 0)}%` }} />
      </div>
      <span className="w-8 shrink-0 text-right text-xs font-semibold tabular-nums text-ink-2">{count}</span>
    </button>
  );
}

function SplitBar({
  left, right,
}: {
  left: { label: string; count: number; barClass: string; onClick?: () => void };
  right: { label: string; count: number; barClass: string; onClick?: () => void };
}) {
  const total = left.count + right.count;
  const leftPct = total > 0 ? (left.count / total) * 100 : 0;
  return (
    <div>
      <div className="flex h-3 overflow-hidden rounded-full bg-raised">
        <button type="button" onClick={left.onClick} className={cn("h-full transition-opacity hover:opacity-80", left.barClass)} style={{ width: `${leftPct}%` }} title={`${left.label}: ${left.count}`} />
        <button type="button" onClick={right.onClick} className={cn("h-full transition-opacity hover:opacity-80", right.barClass)} style={{ width: `${100 - leftPct}%` }} title={`${right.label}: ${right.count}`} />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <button type="button" onClick={left.onClick} className="flex items-center gap-1.5 hover:text-brand-navy-700 dark:hover:text-brand-navy-300">
          <span className={cn("inline-block h-2 w-2 rounded-full", left.barClass)} />
          <span className="font-medium text-ink-2">{left.label}</span>
          <span className="tabular-nums text-ink-muted">{left.count}</span>
        </button>
        <button type="button" onClick={right.onClick} className="flex items-center gap-1.5 hover:text-brand-navy-700 dark:hover:text-brand-navy-300">
          <span className="tabular-nums text-ink-muted">{right.count}</span>
          <span className="font-medium text-ink-2">{right.label}</span>
          <span className={cn("inline-block h-2 w-2 rounded-full", right.barClass)} />
        </button>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-center text-xs text-ink-subtle">{children}</div>;
}

function AgeBreakdown({
  data, onViewTickets,
}: {
  data: { label: string; total: number; rows: { name: string; inc: number; enh: number; total: number }[] };
  onViewTickets: () => void;
}) {
  return (
    <div className="mt-3 rounded-lg border border-line bg-canvas p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-ink-2">
          {data.label} · {data.total} {data.total === 1 ? "ticket" : "tickets"}
        </span>
        <button
          type="button"
          onClick={onViewTickets}
          className="text-[11px] font-medium text-brand-navy-600 hover:text-brand-navy-800 dark:hover:text-brand-navy-300"
        >
          Open in tickets →
        </button>
      </div>
      {data.rows.length === 0 ? (
        <p className="py-3 text-center text-xs text-ink-subtle">No tickets in this band.</p>
      ) : (
        <table className="w-full table-fixed border-collapse text-xs">
          <thead>
            <tr className="text-[11px] text-ink-subtle">
              <th className="py-1 pl-1 text-left font-normal">Group</th>
              <th className="py-1 pr-2 text-right font-normal">Incidents</th>
              <th className="py-1 pr-2 text-right font-normal">Enhancements</th>
              <th className="py-1 pr-1 text-right font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.name} className="border-t border-line text-right">
                <td className="py-1.5 pl-1 text-left text-ink-2 truncate" title={r.name}>{r.name}</td>
                <td className="py-1.5 pr-2 tabular-nums text-ink-3">{r.inc}</td>
                <td className="py-1.5 pr-2 tabular-nums text-ink-3">{r.enh}</td>
                <td className="py-1.5 pr-1 tabular-nums font-medium text-ink">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TypeBreakdown({
  data, onViewTickets,
}: {
  data: { label: string; total: number; rows: { name: string; counts: number[]; total: number }[] };
  onViewTickets: () => void;
}) {
  return (
    <div className="mt-3 rounded-lg border border-line bg-canvas p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-ink-2">
          {data.label} · {data.total} {data.total === 1 ? "ticket" : "tickets"} · by age &amp; group
        </span>
        <button
          type="button"
          onClick={onViewTickets}
          className="text-[11px] font-medium text-brand-navy-600 hover:text-brand-navy-800 dark:hover:text-brand-navy-300"
        >
          Open in tickets →
        </button>
      </div>
      {data.rows.length === 0 ? (
        <p className="py-3 text-center text-xs text-ink-subtle">No tickets of this type.</p>
      ) : (
        <table className="w-full table-fixed border-collapse text-xs">
          <thead>
            <tr className="text-[11px] text-ink-subtle">
              <th className="py-1 pl-1 text-left font-normal">Group</th>
              {JIM_BUCKETS.map((b) => (
                <th key={b.key} className="py-1 pr-2 text-right font-normal">{b.label}</th>
              ))}
              <th className="py-1 pr-1 text-right font-normal">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.name} className="border-t border-line text-right">
                <td className="py-1.5 pl-1 text-left text-ink-2 truncate" title={r.name}>{r.name}</td>
                {r.counts.map((c, i) => (
                  <td key={i} className="py-1.5 pr-2 tabular-nums text-ink-3">{c}</td>
                ))}
                <td className="py-1.5 pr-1 tabular-nums font-medium text-ink">{r.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

type AgingRow = { label: string; group: string; total: number; counts: number[] };

function AgingTypeTable({
  rows, type,
}: {
  rows: AgingRow[];
  type: "incident" | "enhancement";
}) {
  const isInc = type === "incident";
  const head = isInc
    ? { icon: <AlertTriangle className="h-4 w-4 text-ink-subtle" />, name: "Incidents", hint: "target: 30–60 ≤15% · 61+ ≤5%" }
    : { icon: <Lightbulb className="h-4 w-4 text-ink-subtle" />, name: "Enhancements", hint: "target: 30–60 ≤25% · 91+ ≤10%" };

  // Tone for a single cell, given this type's targets.
  const tone = (
    bucketKey: string, pct: number, combined61: number, total: number,
  ): "ok" | "over" | "neutral" => {
    if (total === 0) return "neutral";
    if (isInc) {
      if (bucketKey === "30-60") return pct <= 15 ? "ok" : "over";
      if (bucketKey === "61-90" || bucketKey === "91+") return combined61 <= 5 ? "ok" : "over";
      return "neutral";
    }
    if (bucketKey === "30-60") return pct <= 25 ? "ok" : "over";
    if (bucketKey === "91+") return pct <= 10 ? "ok" : "over";
    return "neutral";
  };
  const toneClass = {
    ok: "text-emerald-700 dark:text-emerald-300",
    over: "text-red-600 dark:text-red-400 font-medium",
    neutral: "text-ink-2",
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        {head.icon}
        <span className="text-sm font-semibold text-ink">{head.name}</span>
        <span className="text-[11px] text-ink-subtle">{head.hint}</span>
      </div>
      <table className="w-full table-fixed border-collapse text-xs">
        <thead>
          <tr className="text-[11px] text-ink-subtle">
            <th className="py-1.5 pl-1.5 text-left font-normal">Group</th>
            <th className="py-1.5 pr-2 text-right font-normal">Total</th>
            {JIM_BUCKETS.map((b) => (
              <th key={b.key} className="py-1.5 pr-2 text-right font-normal">{b.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pcts = r.counts.map((c) => (r.total > 0 ? Math.round((c / r.total) * 100) : 0));
            const i61 = JIM_BUCKETS.findIndex((b) => b.key === "61-90");
            const i91 = JIM_BUCKETS.findIndex((b) => b.key === "91+");
            const combined61 = pcts[i61] + pcts[i91];
            return (
              <tr key={r.group} className="border-t border-line-soft text-right">
                <td className="py-2 pl-1.5 text-left text-ink-2">
                  {r.label}
                  {r.total < 10 && (
                    <span className="ml-1 text-[10px] text-ink-subtle">n={r.total}</span>
                  )}
                </td>
                <td className="py-2 pr-2 tabular-nums text-ink-muted">{r.total}</td>
                {JIM_BUCKETS.map((b, idx) => {
                  const t = tone(b.key, pcts[idx], combined61, r.total);
                  return (
                    <td key={b.key} className={cn("py-2 pr-2 tabular-nums", toneClass[t])}>
                      {r.counts[idx]}{" "}
                      <span className={t === "neutral" ? "text-ink-subtle" : ""}>{pcts[idx]}%</span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
